/**
 * Utility functions for Sony Music availability and regional licensing checks.
 */

let cachedPublicIp = null
let ipFetchPromise = null

/**
 * Fetches public IP address using ipify API with caching.
 */
export async function getPublicIP() {
  if (cachedPublicIp) return cachedPublicIp
  if (ipFetchPromise) return ipFetchPromise

  ipFetchPromise = (async () => {
    try {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
      const timeoutId = controller ? setTimeout(() => controller.abort(), 2500) : null
      const response = await fetch('https://api.ipify.org?format=json', {
        signal: controller?.signal
      })
      if (timeoutId) clearTimeout(timeoutId)
      if (response.ok) {
        const data = await response.json()
        if (data?.ip) {
          cachedPublicIp = data.ip
          return data.ip
        }
      }
    } catch (error) {
      // Silently return empty string if IP service times out or is unreachable
    } finally {
      ipFetchPromise = null
    }
    return ''
  })()

  return ipFetchPromise
}

/**
 * Retrieves the current country code of the user from localStorage or defaults to 'IN'.
 */
export function getUserCurrentCountry() {
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem('user_current_country') || 'IN'
  }
  return 'IN'
}

/**
 * Checks if a Sony Music audio track is available for consumption in the user's region or catalogue.
 * 
 * @param {string} trackId - The ID of the audio track.
 * @param {Array<string>|null} audioAllowedCountries - Array of country codes where track is allowed.
 * @param {string} [userCountry] - Optional user country code (defaults to stored current_country).
 * @returns {{ isSonyTrack: boolean, isAllowed: boolean, reason: string|null, message: string|null }}
 */
export function checkSonyAudioAvailability(trackId, audioAllowedCountries, userCountry) {
  if (!trackId) {
    return {
      isSonyTrack: false,
      isAllowed: true,
      reason: null,
      message: null
    }
  }

  const country = (userCountry || getUserCurrentCountry() || 'IN').toUpperCase()

  if (!audioAllowedCountries || !Array.isArray(audioAllowedCountries) || audioAllowedCountries.length === 0) {
    return {
      isSonyTrack: true,
      isAllowed: true,
      reason: null,
      message: null
    }
  }

  const normalizedAllowed = audioAllowedCountries.map((countryCode) => (typeof countryCode === 'string' ? countryCode.toUpperCase() : countryCode))
  const isAllowed = normalizedAllowed.includes(country)

  if (!isAllowed) {
    return {
      isSonyTrack: true,
      isAllowed: false,
      reason: 'REGION_RESTRICTED',
      message: 'Audio is not available'
    }
  }

  return {
    isSonyTrack: true,
    isAllowed: true,
    reason: null,
    message: null
  }
}

/**
 * Helper to extract trackId and audioAllowedCountries from an item (post/reel/content).
 */
export function getTrackIdAndAllowedCountries(item) {
  if (!item) return { trackId: '', allowedCountries: null }
  const trackId = item?.track_meta?.[0]?.track_id || item?.track_id || ''
  const allowedCountries = item?.audio_allowed_countries || item?.track_meta?.[0]?.audio_allowed_countries || null
  return { trackId, allowedCountries }
}

/**
 * Helper to check if an item's audio is Sony restricted.
 */
export function isItemSonyRestricted(item, userCountry) {
  const { trackId, allowedCountries } = getTrackIdAndAllowedCountries(item)
  const sonyStatus = checkSonyAudioAvailability(trackId, allowedCountries, userCountry)
  return sonyStatus.isSonyTrack && !sonyStatus.isAllowed
}
